import { useState } from 'react';
import { X, Sunrise, Sun, Moon, Repeat, CalendarRange } from 'lucide-react';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const WINDOWS = [
  { key: 'morning', label: 'Morning', hint: '6\u20139 AM', icon: Sunrise },
  { key: 'midday', label: 'Midday', hint: '11 AM\u20132 PM', icon: Sun },
  { key: 'evening', label: 'Evening', hint: '6\u201310 PM', icon: Moon },
];

const everyOtherFrom = (start) => [0, 2, 4, 6].map((step) => (start + step) % 7);

/**
 * Placing a ritual — where the freedom lives, so every choice is made here rather than being
 * undone later. Pick the window, tick the days (quick picks fill them in), and say whether this
 * is one week or the pattern that repeats.
 */
export const PlaceSheet = ({ ritual, weekStart, presetWindow, presetWeekday, onPlace, onClose }) => {
  const suggested = presetWindow
    || (ritual.suggested_window && ritual.suggested_window !== 'any'
        ? ritual.suggested_window : 'morning');
  const seed = presetWeekday ?? new Date().getDay();
  const startDay = seed === 0 ? 6 : seed - 1;   // JS Sunday=0 → our Monday=0

  const [slot, setSlot] = useState(suggested);
  const [days, setDays] = useState(presetWeekday !== undefined ? [presetWeekday] : []);
  const [recurring, setRecurring] = useState(false);
  const [busy, setBusy] = useState(false);

  const toggle = (day) =>
    setDays((d) => (d.includes(day) ? d.filter((x) => x !== day) : [...d, day]));

  const QUICK = [
    { label: 'Just this day', days: [presetWeekday ?? startDay] },
    { label: 'Every day', days: [0, 1, 2, 3, 4, 5, 6] },
    { label: 'Every other day', days: everyOtherFrom(presetWeekday ?? startDay) },
    { label: 'Weekdays', days: [0, 1, 2, 3, 4] },
    { label: 'Weekends', days: [5, 6] },
  ];

  const place = async () => {
    setBusy(true);
    await onPlace({ window: slot, weekdays: days, recurring });
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[125] flex items-end sm:items-center justify-center
                    bg-slate-950/80 backdrop-blur-sm" data-testid="ritual-place-sheet">
      <div className="w-full sm:max-w-lg max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl
                      border border-white/10 bg-[#0b1020] p-6 space-y-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] text-amber-300/70">
              Into your week
            </p>
            <h2 className="text-base md:text-lg font-semibold text-white mt-1">{ritual.title}</h2>
            <p className="text-xs text-white/45 mt-1">
              Windows are energetic zones, not clock slots — put it wherever it truly belongs.
            </p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white transition-colors"
                  data-testid="ritual-place-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-2">
          {WINDOWS.map(({ key, label, hint, icon: Icon }) => (
            <button key={key} onClick={() => setSlot(key)}
                    className={`rounded-xl border px-2 py-3 text-center transition-colors
                      ${slot === key
                        ? 'border-amber-300/60 bg-amber-300/10 text-amber-100'
                        : 'border-white/12 text-white/60 hover:border-white/30'}`}
                    data-testid={`place-window-${key}`}>
              <Icon className="w-4 h-4 mx-auto mb-1" />
              <span className="block text-xs">{label}</span>
              <span className="block text-[10px] text-white/35">{hint}</span>
            </button>
          ))}
        </div>

        <div className="space-y-2.5">
          <span className="text-xs text-white/50">Which days?</span>
          <div className="flex flex-wrap gap-2">
            {QUICK.map(({ label, days: preset }) => (
              <button key={label} onClick={() => setDays(preset)}
                      className="rounded-full border border-white/12 px-3 py-1 text-[11px]
                                 text-white/55 hover:text-amber-100 hover:border-amber-300/40
                                 transition-colors"
                      data-testid={`place-quick-${label.toLowerCase().replace(/ /g, '-')}`}>
                {label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1">
            {DAYS.map((name, index) => (
              <label key={name}
                     className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs
                                 cursor-pointer transition-colors
                       ${days.includes(index)
                         ? 'border-amber-300/50 bg-amber-300/10 text-amber-100'
                         : 'border-white/12 text-white/60 hover:border-white/30'}`}
                     data-testid={`place-day-${index}`}>
                <input type="checkbox" checked={days.includes(index)}
                       onChange={() => toggle(index)} className="accent-amber-400" />
                {name.slice(0, 3)}
              </label>
            ))}
          </div>
          <p className="text-[11px] text-white/30">
            Every other day does not divide evenly into seven, so the quick pick simply ticks the
            days it lands on — change any of them.
          </p>
        </div>

        <div className="space-y-2 border-t border-white/10 pt-4">
          <span className="text-xs text-white/50">How often?</span>
          <div className="grid sm:grid-cols-2 gap-2">
            <button onClick={() => setRecurring(false)}
                    className={`flex items-start gap-2 rounded-xl border px-3 py-3 text-left
                                transition-colors
                      ${!recurring ? 'border-amber-300/60 bg-amber-300/10'
                                   : 'border-white/12 hover:border-white/30'}`}
                    data-testid="place-scope-week">
              <CalendarRange className={`w-4 h-4 mt-0.5 ${!recurring ? 'text-amber-200' : 'text-white/40'}`} />
              <span>
                <span className={`block text-xs ${!recurring ? 'text-amber-100' : 'text-white/70'}`}>
                  This week only
                </span>
                <span className="block text-[10px] text-white/35">
                  Week of {new Date(`${weekStart}T12:00:00`).toLocaleDateString(undefined,
                    { month: 'short', day: 'numeric' })} — other weeks untouched
                </span>
              </span>
            </button>
            <button onClick={() => setRecurring(true)}
                    className={`flex items-start gap-2 rounded-xl border px-3 py-3 text-left
                                transition-colors
                      ${recurring ? 'border-amber-300/60 bg-amber-300/10'
                                  : 'border-white/12 hover:border-white/30'}`}
                    data-testid="place-scope-recurring">
              <Repeat className={`w-4 h-4 mt-0.5 ${recurring ? 'text-amber-200' : 'text-white/40'}`} />
              <span>
                <span className={`block text-xs ${recurring ? 'text-amber-100' : 'text-white/70'}`}>
                  Every week
                </span>
                <span className="block text-[10px] text-white/35">
                  Part of your standing pattern
                </span>
              </span>
            </button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button onClick={place} disabled={busy || !days.length}
                  className="flex-1 rounded-full bg-amber-400 px-5 py-2.5 text-sm font-semibold
                             text-slate-950 hover:bg-amber-300 disabled:opacity-40
                             transition-colors"
                  data-testid="ritual-place-confirm">
            Place on {days.length || 'no'} day{days.length === 1 ? '' : 's'}
            {recurring ? ', every week' : ' this week'}
          </button>
          <button onClick={onClose}
                  className="rounded-full border border-white/15 px-5 py-2.5 text-sm text-white/70
                             hover:text-white hover:border-white/30 transition-colors"
                  data-testid="ritual-place-later">
            Later
          </button>
        </div>
        <p className="text-[11px] text-white/30 text-center">
          Either way it stays in <span className="text-white/50">My rituals</span>, ready to place.
        </p>
      </div>
    </div>
  );
};

/** Pulling a recurring ritual out: this week, or the pattern itself. */
export const RemoveSheet = ({ ritual, weekStart, onRemove, onClose }) => (
  <div className="fixed inset-0 z-[125] flex items-end sm:items-center justify-center
                  bg-slate-950/80 backdrop-blur-sm" data-testid="ritual-remove-sheet">
    <div className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl border border-white/10
                    bg-[#0b1020] p-6 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-base md:text-lg font-semibold text-white">Take it out of…</h2>
          <p className="text-xs text-white/45 mt-1">{ritual.title} repeats every week.</p>
        </div>
        <button onClick={onClose} className="text-white/40 hover:text-white transition-colors"
                data-testid="ritual-remove-close">
          <X className="w-5 h-5" />
        </button>
      </div>
      <button onClick={() => onRemove('week')}
              className="w-full rounded-xl border border-amber-300/40 bg-amber-300/10 px-4 py-3
                         text-left hover:bg-amber-300/20 transition-colors"
              data-testid="remove-scope-week">
        <span className="block text-sm text-amber-100">Just this week</span>
        <span className="block text-[11px] text-white/45">
          Week of {new Date(`${weekStart}T12:00:00`).toLocaleDateString(undefined,
            { month: 'short', day: 'numeric' })} — your pattern stays as it is
        </span>
      </button>
      <button onClick={() => onRemove('all')}
              className="w-full rounded-xl border border-white/12 px-4 py-3 text-left
                         hover:border-rose-300/40 transition-colors"
              data-testid="remove-scope-all">
        <span className="block text-sm text-white/80">Every week</span>
        <span className="block text-[11px] text-white/45">
          Remove it from the pattern entirely
        </span>
      </button>
    </div>
  </div>
);

export default PlaceSheet;
